import { useTranslation } from 'react-i18next';

import { ADMIN_SETTINGS_DUTY_HOUR_TYPES } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

import { DutyHourTypeDataLoaderFeedback } from './components/DutyHourTypeDataLoaderFeedback';

import { useGetColumns } from './hooks/useGetColumns';
import { useGetDutyHourTypesTableData } from './hooks/useGetDutyHourTypesTableData';
import { useCallback, useState } from 'react';
import { isNil, pipe } from 'ramda';
import { FilterComponent } from '../AdminFunctions/filter/FilterComponent';
import { DutyHourType } from './types';
import { Filters } from 'components/admin-portal/Tables/Settings/AdminFunctions/filter/FilterComponent.types';

const DutyHourTypes = () => {
  const { t } = useTranslation();
  const columns = useGetColumns();

  const {
    data: rows,
    isFetching,
    refetch,
    isError
  } = useGetDutyHourTypesTableData();
  const { mutateAsync: deleteRow } = useDeleteById(
    ADMIN_SETTINGS_DUTY_HOUR_TYPES,
    ['dutyHourTypes', 'list']
  );

  const [filteredRows, setFilteredRows] = useState<
    DutyHourType[] | undefined
  >();
  const handleDeleteRow = async (id) => {
    await deleteRow(id);
  };

  const filterBySearch = (data: DutyHourType[], search: string | null) => {
    if (isNil(search)) return data;
    return data.filter(
      (item) =>
        item.code?.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase()) ||
        item.dcc?.dccDescription.toLowerCase().includes(search.toLowerCase())
    );
  };

  const handleFilterChange = useCallback(
    async (filters: Filters) => {
      if (isFetching) return rows;
      const { search } = filters;

      const filtered = await pipe((data) => filterBySearch(data, search))(rows);

      setFilteredRows(filtered);
    },
    [rows]
  );

  return (
    <>
      <FilterComponent
        handleFilterChange={handleFilterChange}
        dependencies={[rows]}
      />
      <TableCRUDLayout
        FormComponent={DutyHourTypeDataLoaderFeedback}
        rows={filteredRows}
        loading={isFetching}
        columns={columns}
        confirmationTitle={t('dutyHourTypes.delete.title')}
        confirmationBody={t('dutyHourTypes.delete.body')}
        initialState={{
          sorting: { sortModel: [{ field: 'code', sort: 'asc' }] }
        }}
        onDeleteClick={handleDeleteRow}
        hasLoadingError={isError}
        onLoadingError={refetch}
      />
    </>
  );
};

export default DutyHourTypes;
